import CustomIcon from '@/common/components/base/customIcon/CustomIcon';
import CircularLoading from '@/common/components/base/Loading/CircularLoading';
import CardSquare from '@/common/components/cardSquare/CardSquare';
import getDamagedTypeQueryOption from '@/common/queries/getDamagedType/getDamagedTypeQueryOPtion';
import { ADJUSTER_REPORT_DAMAGED_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/damaged/damagedLinks';
import Costs from '@/modules/adjusterReportModules/IncidentParticipants/assets/iconComponents/Costs';
import SubstitutionParts from '@/modules/adjusterReportModules/IncidentParticipants/assets/iconComponents/SubstitutionParts';
import VisitInformation from '@/modules/adjusterReportModules/IncidentParticipants/assets/iconComponents/visitInformation';
import Wages from '@/modules/adjusterReportModules/IncidentParticipants/assets/iconComponents/Wages';
import { ExpertEntryTabsQueryResponseType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getExpertTabs/getExpertTabsQuery.type';
import { getExpertTabsQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getExpertTabs/getExpertTabsQueryOption';
import { HandleRoutesNavigation } from '@/modules/adjusterReportModules/IncidentParticipants/utils/HandleRoutesNavigation';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Grid } from 'styled-system/jsx';
import { token } from 'styled-system/tokens';

const ExpertEntryTabs = () => {
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const { data, isPending } = useQuery(
    getExpertTabsQueryOptions({
      id: +id!,
      damaged_id: +damagedId!,
      source: 'pwa',
    }),
  );

  const { data: damaged } = useQuery(
    getDamagedTypeQueryOption({ damaged_pk: damagedId }),
  );

  const renderRoute = (item: ExpertEntryTabsQueryResponseType) => {
    const damagedType = damaged?.damaged_type;
    const isEditable = item?.editable;

    const path: Record<ExpertEntryTabsQueryResponseType['name'], string> = {
      accessory: '',
      visit_form: '',
      visit_information:
        damagedType === 'VEHICLE'
          ? HandleRoutesNavigation({
              isEditable: isEditable,
              edit: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).EXPERT.visitInformation,
              show: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).EXPERT.visitInformationShow,
            })
          : HandleRoutesNavigation({
              isEditable: isEditable,
              edit: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).OTHER_EXPERT.visitInformation,
              show: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).OTHER_EXPERT.visitInformationShow,
            }),
      costs:
        damagedType === 'VEHICLE'
          ? HandleRoutesNavigation({
              isEditable: isEditable,
              edit: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).EXPERT.costs,
              show: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).EXPERT.costsShow,
            })
          : HandleRoutesNavigation({
              isEditable: isEditable,
              edit: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).EXPERT.otherCosts,
              show: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                damagedId: +damagedId!,
                id: +id!,
              }).EXPERT.otherCostsShow,
            }),
      substitution_parts: HandleRoutesNavigation({
        isEditable: isEditable,
        edit: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
          damagedId: +damagedId!,
          id: +id!,
        }).EXPERT.substitutionParts,
        show: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
          damagedId: +damagedId!,
          id: +id!,
        }).EXPERT.substitutionPartsShow,
      }),
      wages: HandleRoutesNavigation({
        isEditable: isEditable,
        edit: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
          damagedId: +damagedId!,
          id: +id!,
        }).EXPERT.wages,
        show: ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
          damagedId: +damagedId!,
          id: +id!,
        }).EXPERT.wagesShow,
      }),
    };

    return path[item.name];
  };

  const goToFile = (item: ExpertEntryTabsQueryResponseType) => {
    navigate(renderRoute(item));
  };
  return (
    <>
      {isPending ? (
        <Box
          mt="56px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularLoading color="colors.purple-primary" />
        </Box>
      ) : (
        <Box mt="56px">
          <Grid
            className={css({
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '6px',
            })}
          >
            {data?.map((item) => (
              <Box key={item.name}>
                <CardSquare
                  disabled={item?.is_card_active}
                  title={item?.fa_name}
                  onClick={() => goToFile(item)}
                  renderIcon={() => {
                    switch (item.name) {
                      case 'visit_information':
                        return (
                          <CustomIcon
                            backgroundColor={
                              item?.is_card_active
                                ? token('colors.orange-3')
                                : token('colors.gray-3')
                            }
                            width="42px"
                            height="42px"
                          >
                            <VisitInformation
                              fill={
                                item?.is_card_active
                                  ? token('colors.orange-3')
                                  : token('colors.gray-3')
                              }
                              stroke={
                                item?.is_card_active
                                  ? token('colors.orange')
                                  : token('colors.gray')
                              }
                            />
                          </CustomIcon>
                        );
                      case 'wages':
                        return (
                          <Wages
                            fill={
                              item?.is_card_active
                                ? token('colors.purple-6')
                                : token('colors.gray-3')
                            }
                            stroke={
                              item?.is_card_active
                                ? token('colors.purple-primary')
                                : token('colors.gray')
                            }
                          />
                        );
                      case 'substitution_parts':
                        return (
                          <SubstitutionParts
                            fill={
                              item?.is_card_active
                                ? token('colors.green-4')
                                : token('colors.gray-3')
                            }
                            stroke={
                              item?.is_card_active
                                ? token('colors.green')
                                : token('colors.gray')
                            }
                          />
                        );
                      case 'costs':
                        return (
                          <Costs
                            fill={
                              item?.is_card_active
                                ? token('colors.purple-6')
                                : token('colors.gray-3')
                            }
                            stroke={
                              item?.is_card_active
                                ? token('colors.purple-primary')
                                : token('colors.gray')
                            }
                          />
                        );
                      default:
                        return null;
                    }
                  }}
                />
              </Box>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );
};

export default ExpertEntryTabs;
